import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
    fetchAllEmployeePermission,
    fetchRoleList,
    fetchRolesWithType,
    saveCustomRole,
    fetchRolePermissionsByRoleId,
} from '../../store/actions/auth';
import LoaderSpinner from '../common/Loader';
import ActionItems from '../common/showActions';

const permissionKey = (featureId, subFeatureId, permissionId) => `${featureId}|${subFeatureId}|${permissionId}`;

const collectCheckedKeys = (features) => {
    const checked = new Set();
    (features || []).forEach((feature) => {
        (feature?.SubFeatures || []).forEach((subFeature) => {
            (subFeature?.Permissions || subFeature?.permissions || []).forEach((permission) => {
                if (permission?.permissionHave) {
                    checked.add(permissionKey(feature.featureId, subFeature.subFeatureId, permission.permissionId));
                }
            });
        });
    });
    return checked;
};

const buildPermissionsPayload = (features, checkedKeys) =>
    _.flatMap(features, (feature) =>
        _.flatMap(feature.SubFeatures || [], (subFeature) =>
            (subFeature.Permissions || []).map((permission) => ({
                featureId: feature.featureId,
                subFeatureId: subFeature.subFeatureId,
                permissionId: permission.permissionId,
                permissionHave: checkedKeys.has(
                    permissionKey(feature.featureId, subFeature.subFeatureId, permission.permissionId)
                ),
            }))
        )
    );

/**
 * Custom Roles screen (Roles & Permissions):
 * - list view with Default / Custom sub tabs, search and type chips
 * - create / edit view with the permission tree (feature -> sub feature -> action)
 *   where parent checkboxes cascade to every child action.
 */
const CustomRoles = ({ dispatch }) => {
    const { allEmployeePermission } = useSelector((state) => state?.auth);

    const [view, setView] = useState('list'); // 'list' | 'editor'
    const [subTab, setSubTab] = useState('default'); // 'default' | 'custom'
    const [roles, setRoles] = useState([]);
    const [search, setSearch] = useState('');
    const [showLoader, setShowLoader] = useState(false);

    // editor state
    const [editingRole, setEditingRole] = useState(null); // null = create, {id,name,isCustom} = view/edit
    const [roleName, setRoleName] = useState('');
    const [baseRoleId, setBaseRoleId] = useState('');
    const [checkedKeys, setCheckedKeys] = useState(new Set());
    const [expandedFeatures, setExpandedFeatures] = useState(new Set());
    const [expandedSubFeatures, setExpandedSubFeatures] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const isReadOnly = !!editingRole && !editingRole.isCustom;

    const loadRoles = useCallback(async () => {
        try {
            setShowLoader(true);
            const response = await fetchRolesWithType();
            if (response?.data?.Status === 200) {
                setRoles(response?.data?.EntityList || []);
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Unable to load roles');
            }
        } catch (error) {
            toast.error('Unable to load roles');
        } finally {
            setShowLoader(false);
        }
    }, []);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    useEffect(() => {
        if (!allEmployeePermission.length) {
            dispatch(fetchAllEmployeePermission());
        }
    }, [allEmployeePermission, dispatch]);

    const filteredRoles = useMemo(() => {
        const q = (search || '').trim().toLowerCase();
        return roles
            .filter((r) => (subTab === 'custom' ? r.isCustom : !r.isCustom))
            .filter((r) => !q || (r.name || '').toLowerCase().includes(q));
    }, [roles, subTab, search]);

    const defaultRoleOptions = useMemo(() => roles.filter((role) => !role.isCustom), [roles]);

    /* ---------- editor helpers ---------- */

    const leafKeysOfSubFeature = useCallback((feature, subFeature) => {
        return (subFeature?.Permissions || []).map((p) =>
            permissionKey(feature.featureId, subFeature.subFeatureId, p.permissionId)
        );
    }, []);

    const leafKeysOfFeature = useCallback(
        (feature) => _.flatMap(feature?.SubFeatures || [], (sf) => leafKeysOfSubFeature(feature, sf)),
        [leafKeysOfSubFeature]
    );

    const allPermissionKeys = useMemo(
        () => _.flatMap(allEmployeePermission || [], (feature) => leafKeysOfFeature(feature)),
        [allEmployeePermission, leafKeysOfFeature]
    );

    const allPermissionsSelected = allPermissionKeys.length > 0 && allPermissionKeys.every((key) => checkedKeys.has(key));

    const openEditor = async (role) => {
        setEditingRole(role || null);
        setRoleName(role?.name || '');
        setBaseRoleId(role?.baseRoleId || '');
        setCheckedKeys(new Set());
        setExpandedFeatures(new Set());
        setExpandedSubFeatures(new Set());
        setView('editor');

        if (role?.id) {
            try {
                setShowLoader(true);
                const response = await fetchRolePermissionsByRoleId(role.id);
                if (response?.data?.Status === 200) {
                    setCheckedKeys(collectCheckedKeys(response?.data?.EntityList));
                } else {
                    toast.error(response?.data?.Message?.AppStatusDescription || 'Unable to load role permissions');
                }
            } catch (error) {
                toast.error('Unable to load role permissions');
            } finally {
                setShowLoader(false);
            }
        }
    };

    const closeEditor = () => {
        setView('list');
        setEditingRole(null);
        setRoleName('');
        setBaseRoleId('');
        setCheckedKeys(new Set());
    };

    const toggleKeys = (keys, checked) => {
        setCheckedKeys((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (checked ? next.add(k) : next.delete(k)));
            return next;
        });
    };

    const toggleExpandFeature = (featureId) => {
        setExpandedFeatures((prev) => {
            const next = new Set(prev);
            if (next.has(featureId)) next.delete(featureId);
            else next.add(featureId);
            return next;
        });
    };

    const toggleExpandSubFeature = (featureId, subFeatureId) => {
        const key = `${featureId}|${subFeatureId}`;
        setExpandedSubFeatures((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const expandAll = (open) => {
        if (open) {
            setExpandedFeatures(new Set(allEmployeePermission.map((f) => f.featureId)));
            setExpandedSubFeatures(
                new Set(
                    _.flatMap(allEmployeePermission, (f) =>
                        (f.SubFeatures || []).map((sf) => `${f.featureId}|${sf.subFeatureId}`)
                    )
                )
            );
        } else {
            setExpandedFeatures(new Set());
            setExpandedSubFeatures(new Set());
        }
    };

    const handleSave = async () => {
        if (saving) return; // guard against double-submit (button disable lags one render)
        const name = (roleName || '').trim();
        if (!name) {
            toast.error('Please enter a role name');
            return;
        }
        // Instant client-side duplicate check against the loaded role list (case-insensitive)
        const collision = roles.find(
            (r) => r.id !== (editingRole?.id || 0) && (r.name || '').trim().toLowerCase() === name.toLowerCase()
        );
        if (collision) {
            toast.error(`A ${collision.isCustom ? 'custom' : 'default'} role named "${collision.name}" already exists`);
            return;
        }
        if (!baseRoleId) {
            toast.error('Please select a base role');
            return;
        }
        const permissions = buildPermissionsPayload(allEmployeePermission, checkedKeys);
        try {
            setSaving(true);
            const response = await saveCustomRole({
                id: editingRole?.id || 0,
                roleName: name,
                baseRoleId: Number(baseRoleId),
                permissions,
            });
            if (response?.data?.Status === 200) {
                toast.success(editingRole?.id ? 'Custom role updated successfully' : 'Custom role created successfully');
                setSubTab('custom');
                closeEditor();
                loadRoles();
                // Refresh matrix + role dropdown data so the new role shows everywhere.
                dispatch(fetchAllEmployeePermission());
                dispatch(fetchRoleList());
            } else if (response?.data?.Entity === -1) {
                toast.error(`A role named "${name}" already exists`);
                loadRoles(); // refresh so the colliding role is visible in the list
            } else if (response?.data?.Entity === -3) {
                toast.error('Please select a valid base role');
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Unable to save custom role');
            }
        } catch (error) {
            toast.error('Unable to save custom role');
        } finally {
            setSaving(false);
        }
    };

    /* ---------- renderers ---------- */

    const renderList = () => (
        <div>
            <div className="roleSubTabs">
                <button
                    className={`roleSubTab ${subTab === 'default' ? 'active' : ''}`}
                    onClick={() => setSubTab('default')}
                >
                    Default Roles
                </button>
                <button
                    className={`roleSubTab ${subTab === 'custom' ? 'active' : ''}`}
                    onClick={() => setSubTab('custom')}
                >
                    Custom Roles
                </button>
            </div>

            <div className="customRoleHead">
                <input
                    className="customRoleSearch"
                    placeholder="Search by Role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn cus-seconday-bg-btn createCustomRoleBtn" onClick={() => openEditor(null)}>
                    Create Custom Role
                </button>
            </div>

            <table className="customRoleTable" data-testid="customRoleTable">
                <thead>
                    <tr>
                        <th>Role</th>
                        <th style={{ width: '140px' }}>Type</th>
                        <th className="act" style={{ width: '140px' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredRoles.map((role) => (
                        <tr key={role.id}>
                            <td>{role.name}</td>
                            <td>
                                <span className={`typeChip ${role.isCustom ? 'custom' : 'default'}`}>
                                    {role.isCustom ? 'Custom' : 'Default'}
                                </span>
                            </td>
                            <td className="act">
                                <ActionItems
                                    type={role.isCustom ? 'Edit' : 'View'}
                                    item={role}
                                    handleAction={() => openEditor(role)}
                                />
                            </td>
                        </tr>
                    ))}
                    {!filteredRoles.length && (
                        <tr>
                            <td colSpan={3} className="noRolesFound">No roles found</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderPermissionRow = (feature, subFeature, permission) => {
        const key = permissionKey(feature.featureId, subFeature.subFeatureId, permission.permissionId);
        return (
            <div className="permTreeRow lvl2" key={key}>
                <div className="permTreeName indent2">
                    <span>{permission.permissionName}</span>
                </div>
                <div className="permAllow">
                    <Form.Check
                        type="checkbox"
                        className="permTreeCheck"
                        checked={checkedKeys.has(key)}
                        disabled={isReadOnly}
                        onChange={(e) => toggleKeys([key], e.target.checked)}
                    />
                </div>
            </div>
        );
    };

    const renderSubFeatureRows = (feature, subFeature) => {
        const sfKey = `${feature.featureId}|${subFeature.subFeatureId}`;
        const keys = leafKeysOfSubFeature(feature, subFeature);
        const isExpanded = expandedSubFeatures.has(sfKey);
        const checkedCount = keys.filter((k) => checkedKeys.has(k)).length;
        return (
            <React.Fragment key={sfKey}>
                <div className="permTreeRow lvl1">
                    <button
                        className="permTreeName indent1 permTreeToggle"
                        onClick={() => toggleExpandSubFeature(feature.featureId, subFeature.subFeatureId)}
                    >
                        <i className={`fa ${isExpanded ? 'fa-angle-down' : 'fa-angle-right'} mr-2`} aria-hidden="true"></i>
                        {subFeature.subFeatureName}
                    </button>
                    <div className="permAllow">
                        <Form.Check
                            type="checkbox"
                            className="permTreeCheck subFeatureCheck"
                            checked={keys.length > 0 && checkedCount === keys.length}
                            disabled={isReadOnly}
                            onChange={(e) => toggleKeys(keys, e.target.checked)}
                        />
                    </div>
                </div>
                {isExpanded &&
                    (subFeature.Permissions || []).map((permission) => renderPermissionRow(feature, subFeature, permission))}
            </React.Fragment>
        );
    };

    const renderFeatureRows = (feature) => {
        const keys = leafKeysOfFeature(feature);
        const isExpanded = expandedFeatures.has(feature.featureId);
        const checkedCount = keys.filter((k) => checkedKeys.has(k)).length;
        return (
            <React.Fragment key={feature.featureId}>
                <div className="permTreeRow lvl0">
                    <button className="permTreeName permTreeToggle" onClick={() => toggleExpandFeature(feature.featureId)}>
                        <i className={`fa ${isExpanded ? 'fa-angle-down' : 'fa-angle-right'} mr-2`} aria-hidden="true"></i>
                        {feature.featureName}
                    </button>
                    <div className="permAllow">
                        <Form.Check
                            type="checkbox"
                            className="permTreeCheck featureCheck"
                            checked={keys.length > 0 && checkedCount === keys.length}
                            disabled={isReadOnly}
                            onChange={(e) => toggleKeys(keys, e.target.checked)}
                        />
                    </div>
                </div>
                {isExpanded && (feature.SubFeatures || []).map((subFeature) => renderSubFeatureRows(feature, subFeature))}
            </React.Fragment>
        );
    };

    const getEditorTitle = () => {
        if (!editingRole) return 'Create Custom Role';
        return editingRole.isCustom ? 'Edit Custom Role' : 'View Role';
    };

    const renderEditor = () => (
        <div>
            <div className="crEditorBar">
                <button className="backLink" onClick={closeEditor}>
                    &lt; Roles
                </button>
                <input
                    className="crRoleNameInput"
                    placeholder="Enter Role Name"
                    value={roleName}
                    disabled={isReadOnly}
                    onChange={(e) => setRoleName(e.target.value)}
                />
                {!isReadOnly && (
                    <select
                        className="crRoleNameInput"
                        value={baseRoleId}
                        onChange={(e) => setBaseRoleId(e.target.value)}
                        required
                    >
                        <option value="">Select Base Role *</option>
                        {defaultRoleOptions.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                )}
                {!isReadOnly && (
                    <button className="btn cus-seconday-bg-btn saveCustomRoleBtn" disabled={saving} onClick={handleSave}>
                        {editingRole?.id ? 'Save Changes' : 'Create Custom Role'}
                    </button>
                )}
            </div>

            <div className="permTreeCard">
                <div className="permTreeCardHead">
                    <h3>{getEditorTitle()}</h3>
                    <div className="permTreeCardActions">
                        <button className="btn cus-primary-transparent-btn" onClick={() => expandAll(true)}>
                            Expand All
                        </button>
                        <button className="btn cus-primary-transparent-btn" onClick={() => expandAll(false)}>
                            Collapse All
                        </button>
                    </div>
                </div>
                <div className="permTreeHead">
                    <div>Set Permission / Select All</div>
                    <div className="permAllow">
                        <Form.Check
                            type="checkbox"
                            className="permTreeCheck"
                            checked={allPermissionsSelected}
                            disabled={isReadOnly || allPermissionKeys.length === 0}
                            onChange={(e) => toggleKeys(allPermissionKeys, e.target.checked)}
                        />
                    </div>
                </div>
                <div data-testid="permTree">
                    {allEmployeePermission.map((feature) => renderFeatureRows(feature))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="customRolesWrapper">
            {showLoader && <LoaderSpinner />}
            {view === 'list' ? renderList() : renderEditor()}
        </div>
    );
};

CustomRoles.propTypes = {
    dispatch: PropTypes.func.isRequired,
};

export default CustomRoles;
